import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableSlotGroup,
  RichTextContent,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnPrimary, linkTarget } from '../utils/content';

const imageDef: SlotGroupDefinition = {
  label: 'Welcome Photo',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt: 'A warm lifestyle photo that sums up living here — people, community spaces, or the setting — rather than an empty room.',
    context: 'Square photo beside the welcome intro, directly below the home hero. Should differ from the hero image.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'Welcome Button',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Schedule a Tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'schedule_tour_clicked' },
  },
};

export function PropertyOverview() {
  const photo = useSlotGroup('propertyoverview.image', imageDef);
  const cta = useSlotGroup('propertyoverview.cta', ctaDef);

  return (
    <section className="bg-background py-14 md:py-20">
      <div className="mx-auto grid max-w-[1180px] items-center gap-10 px-6 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] md:gap-12 lg:gap-16 lg:px-10">
        <EditableSlotGroup groupPath="propertyoverview.image" definition={imageDef}>
          <div className="js-reveal-img js-zoom relative mx-auto aspect-square w-full max-w-[520px] overflow-hidden bg-muted">
            <VibesitesImage
              url={photo.image || ''}
              alt={photo.alt || ''}
              sizes="(min-width: 768px) 42vw, 100vw"
              className="h-full w-full object-cover"
            />
            <span aria-hidden className="js-frame-line pointer-events-none absolute inset-[11%] border border-white/70" />
          </div>
        </EditableSlotGroup>

        <div>
          <RichTextContent
            path="propertyoverview.heading"
            as="h2"
            className="js-reveal js-d1 js-heading text-[28px] text-foreground sm:text-[32px] lg:text-[36px]"
          />
          <RichTextContent
            path="propertyoverview.body"
            as="div"
            className="js-reveal js-d2 js-copy mt-6 text-foreground [&_p+p]:mt-4"
          />
          <div className="js-reveal js-d3 mt-9 flex justify-center md:justify-start lg:justify-center">
            <EditableSlotGroup groupPath="propertyoverview.cta" definition={ctaDef}>
              <a
                href={cta.url || '/schedule-a-tour'}
                target={linkTarget(cta.url, cta._target)}
                data-vibesite-event={cta.vibesite_event || undefined}
                className={btnPrimary}
              >
                {cta.label || 'Schedule a Tour'}
              </a>
            </EditableSlotGroup>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Welcome Intro',
  tokens: {
    'propertyoverview.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultField: 'property_home_romance_title',
      defaultValue:
        '<p>Bringing the best of <span data-type="field-token" data-field="property_address_city"></span> to <span data-type="field-token" data-field="property_name"></span></p>',
    },
    'propertyoverview.body': {
      label: 'Intro Copy',
      type: 'richtext',
      defaultField: 'property_short_description',
      suggestedFields: ['property_short_description', 'property_long_description'],
      defaultValue:
        '<p>Forward-thinking, modern design meets warm, refined spaces with thoughtfully integrated natural elements. Come live at the hub of culture and community.</p>',
    },
  },
  slotGroups: {
    'propertyoverview.image': imageDef,
    'propertyoverview.cta': ctaDef,
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
